import { FormModal } from '@/components/form-modal.js';
import { FormCheckbox } from '@/components/forms/form-checkbox.js';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert.js';
import { Button } from '@/components/ui/button.js';
import { DialogDescription } from '@/components/ui/dialog.js';
import { useToast } from '@/components/ui/hooks/use-toast.js';
import { deleteSchool, getSchoolDeleteEligibility, getSchools, invalidateSchoolQueries } from '@/lib/api-calls.js';
import { airServerErrorFrom } from '@/lib/error-handling.js';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { z } from 'zod';

const formSchema = z.object({
  confirmed: z.boolean().default(false),
});

export function DeleteSchool({ schoolId, schoolName }: { schoolId: string; schoolName: string }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data: eligibility, isPending: isEligibilityPending } = useQuery(getSchoolDeleteEligibility(schoolId));

  const { mutateAsync: deleteSchoolMutate, isPending: isDeletePending } = useMutation({
    mutationFn: deleteSchool,
  });

  const canDelete = eligibility?.canDelete ?? false;
  const isPending = isEligibilityPending || isDeletePending;

  const onSubmit = async ({
    formData,
    closeModal,
  }: {
    formData: z.infer<typeof formSchema>;
    closeModal: () => void;
  }) => {
    if (!canDelete) {
      return;
    }

    if (!formData.confirmed) {
      toast({
        title: 'Please confirm that you understand this school will be removed before continuing.',
      });
      return;
    }

    try {
      await deleteSchoolMutate(schoolId);
      closeModal();
      queryClient.invalidateQueries(getSchools());
      invalidateSchoolQueries(queryClient);
      navigate({
        to: '/orgs/schools',
        search: {
          pageIndex: 0,
          pageSize: 50,
        },
      });
    } catch (e) {
      const error = airServerErrorFrom(e);
      toast({
        variant: 'destructive',
        title: 'Failed to delete school',
        description: error?.userFacingMessage,
      });
    }
  };

  return (
    <FormModal
      modalTitle="Delete school"
      dataTestId="school-modal-delete"
      trigger={
        <Button type="button" variant="destructive" data-testid="school-button-delete">
          Delete
        </Button>
      }
      onSubmit={onSubmit}
      dialogContentProps={{ className: 'max-w-md' }}
      submitButtonProps={{
        variant: 'destructive',
        children: 'Delete school',
        loading: isPending,
        disabled: isEligibilityPending || !canDelete,
      }}
      formSchema={formSchema}
      confirmBtnLabel="Delete school"
      defaultValues={{ confirmed: false }}
    >
      {({ form }) => (
        <div className="my-6 space-y-4">
          {isEligibilityPending ? (
            <DialogDescription role="status">Checking whether this school can be deleted…</DialogDescription>
          ) : !canDelete && eligibility?.reasons.length ? (
            <Alert variant="destructive">
              <AlertTitle>This school cannot be deleted</AlertTitle>
              <AlertDescription>
                <ul className="list-disc pl-4 space-y-1">
                  {eligibility.reasons.map((reason) => (
                    <li key={reason}>{reason}</li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          ) : (
            <DialogDescription>
              {schoolName} will be removed from active and inactive school lists. Students, schedules, classes,
              sessions, reports, and the Edlink link are not deleted.
            </DialogDescription>
          )}
          {canDelete && (
            <FormCheckbox
              name="confirmed"
              label="I understand this school will be removed from all lists"
              form={form}
              horizontal
            />
          )}
        </div>
      )}
    </FormModal>
  );
}
